/**
 * PromoteCounterpartyModal — P3-9.6.
 *
 * Single reusable modal that promotes any of the 5 counterparty types
 * (supplier / client / contact / customer / lead) into another role.
 * Keeps the original record intact; links via cross_references.
 *
 * Usage:
 *   <PromoteCounterpartyModal
 *      open={open}
 *      onClose={fn}
 *      source={{ prefixed_id: 'client:...', name: 'Clariton', source: 'client' }}
 *      onPromoted={(result) => ...}
 *   />
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  X, ArrowRight, Briefcase, Users, Building2, Sparkles, UserPlus, CheckCircle2,
} from 'lucide-react';

import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TARGETS = [
  { value: 'supplier', label: 'Supplier', icon: Briefcase, color: '#023E8A', hint: 'Gains ability to receive POs, GRNs and invoicing as vendor.' },
  { value: 'partner', label: 'Partner', icon: Sparkles, color: '#D97706', hint: 'Stored as a Supplier with Partner category. For JV / strategic alliances.' },
  { value: 'client', label: 'Client', icon: Users, color: '#0F766E', hint: 'CRM Client with industry, tags and relationship tracking.' },
  { value: 'customer', label: 'Finance Customer', icon: Building2, color: '#7C3AED', hint: 'Appears in the Finance module for invoicing.' },
  { value: 'contact', label: 'Contact', icon: UserPlus, color: '#6366F1', hint: 'Lightweight CRM contact for follow-ups.' },
  { value: 'lead', label: 'Lead', icon: UserPlus, color: '#DC2626', hint: 'Treated as a new prospect in the CRM pipeline.' },
];

const SOURCE_BLOCKS = {
  // For each source role, which same-role(s) must not be selectable
  supplier: ['supplier'],
  partner: ['supplier', 'partner'],
  client: ['client'],
  contact: ['contact'],
  customer: ['customer'],
  lead: ['lead'],
};

const PromoteCounterpartyModal = ({ open, onClose, source, onPromoted }) => {
  const { toast } = useToast();
  const [target, setTarget] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const blockedTargets = useMemo(() => {
    const key = source?.is_partner ? 'partner' : (source?.source || '');
    return SOURCE_BLOCKS[key] || [];
  }, [source]);

  if (!open || !source) return null;

  const handleSubmit = async () => {
    if (!target) {
      toast({ variant: 'destructive', title: 'Select a target role' });
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const r = await axios.post(
        `${API_URL}/api/crm/counterparties/${encodeURIComponent(source.prefixed_id || source.id)}/promote`,
        { target },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: r.data?.status === 'existed' ? 'Already linked' : 'Promoted successfully',
        description: `${source.name} is now also a ${target}.`,
      });
      if (onPromoted) onPromoted(r.data);
      onClose();
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Promotion failed',
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="promote-modal"
    >
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-[#023E8A] to-[#0353A4] text-white rounded-t-lg">
          <div className="flex items-center gap-3">
            <ArrowRight className="h-5 w-5" />
            <div>
              <h2 className="text-lg font-bold">Promote counterparty</h2>
              <p className="text-xs opacity-90">
                {source.name}
                <span className="ml-2 rounded bg-white/20 px-1.5 py-0.5 text-[10px] uppercase">
                  {source.is_partner ? 'partner' : source.source}
                </span>
              </p>
            </div>
          </div>
          <button onClick={onClose} className="opacity-80 hover:opacity-100" data-testid="promote-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">
            This creates a linked record in the target directory. The original <strong>{source.name}</strong> record
            stays unchanged — all historical references (POs, invoices, deals) continue to work.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {TARGETS.map((t) => {
              const disabled = blockedTargets.includes(t.value);
              const active = target === t.value && !disabled;
              const Icon = t.icon;
              return (
                <button
                  key={t.value}
                  type="button"
                  disabled={disabled}
                  onClick={() => setTarget(t.value)}
                  data-testid={`promote-target-${t.value}`}
                  className={`group flex items-start gap-3 rounded-lg border px-3 py-3 text-left transition-all ${
                    disabled
                      ? 'opacity-40 cursor-not-allowed border-slate-200 bg-slate-50'
                      : active
                        ? 'border-[#FF6B35] bg-orange-50 shadow-sm'
                        : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                  }`}
                >
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md"
                    style={{ backgroundColor: `${t.color}15`, color: t.color }}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-800">{t.label}</span>
                      {active && <CheckCircle2 className="h-4 w-4 text-[#FF6B35]" />}
                    </div>
                    <p className="mt-0.5 text-[11px] text-slate-500 leading-tight">{t.hint}</p>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
            <strong>Preview mapping:</strong> Name, email, phone, address, TRN and contact person from the{' '}
            <strong>{source.source}</strong> record will be copied to the new <strong>{target || '—'}</strong>.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="promote-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!target || submitting}
            className="bg-[#FF6B35] hover:bg-[#E55A24] text-white"
            data-testid="promote-submit"
          >
            {submitting ? 'Promoting…' : `Promote to ${target || '…'}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PromoteCounterpartyModal;
